import { useMemo, useRef, useState } from "react";
import { FileText, Pin, Plus, Upload, Lock, Download, Hash, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useVault } from "@/context/VaultContext";
import { addAttachment, buildIndex, createNote, saveDraft, stampVersion } from "@/lib/store";
import { scanPatch, scanWithToast } from "@/lib/scan";
import { kindOf, normalizeFile } from "@/lib/util";
import { AutoScanToggle, useAutoScan } from "./AutoScanToggle";
import { DeliveryBoard } from "./DeliveryBoard";
import { buildTextPdf } from "@/lib/compile";
import { docName, downloadBlob, stamp } from "@/lib/util";
import { noteColor } from "@/lib/calendar";

function NoteRow({ n, onOpen }) {
  const { folderName } = useVault();
  return (
    <button type="button" onClick={onOpen} data-testid={`note-row-${n.id}`}
      className="sv-rise w-full text-left rounded-lg border bg-card p-4 hover:border-primary/50 transition-colors">
      <div className="flex items-start gap-3">
        <span className="mt-1.5 h-3 w-3 rounded-full shrink-0" style={{ background: noteColor(n) }} />
        <div className="min-w-0 flex-1">
          <p className="font-heading font-semibold truncate">{n.title || `${folderName(n.folderId)} - ${stamp(new Date(n.createdAt)).slice(0, 10)}`}</p>
          <p className="text-sm text-muted-foreground line-clamp-1 mt-1">{n.summary?.sentence || "No text yet"}</p>
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 font-mono text-[11px] tracking-wider text-muted-foreground">
            <span>{stamp(new Date(n.createdAt))}</span>
            {(n.ref || n.summary?.ref) && <span className="flex items-center gap-1"><Hash className="h-3 w-3" />{n.ref || n.summary.ref}</span>}
            {n.receipt?.status && <span className="text-primary">{n.receipt.status}</span>}
            {n.scheduledFor && <span data-testid={`note-row-scheduled-${n.id}`}>{n.entryType || "Note"} · {n.scheduledFor}</span>}
          </div>
        </div>
        <Badge variant="outline" className="shrink-0 gap-1 font-mono text-[10px]"><Lock className="h-3 w-3" />v{n.versionCount || 0}</Badge>
      </div>
    </button>
  );
}

function IndexCard({ folder, list, profile }) {
  const [open, setOpen] = useState(false);
  const md = useMemo(() => buildIndex(folder, list), [folder, list]);
  const name = (ext) => docName({ profile, folderName: folder.name, title: "Folder Summary", ref: "", ext });
  const downloadTxt = () => downloadBlob(new Blob([md], { type: "text/plain" }), name("txt"));
  const downloadPdf = () => downloadBlob(buildTextPdf(md), name("pdf"));
  return (
    <>
      <div className="rounded-lg border border-primary/35 bg-accent/40 p-4" data-testid="folder-index-card">
        <div className="flex items-center gap-2">
          <Pin className="h-4 w-4 text-primary" />
          <span className="font-heading text-sm font-semibold">Folder Summary</span>
        </div>
        <p className="mt-3 text-sm text-muted-foreground whitespace-pre-wrap line-clamp-4">{md.split("\n").slice(3, 7).join("\n") || "No entries yet."}</p>
        <div className="flex flex-wrap gap-2 mt-3">
          <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="index-view-button"><FileText className="h-4 w-4 mr-1" />View</Button>
          <Button size="sm" variant="ghost" onClick={downloadPdf} data-testid="index-download-pdf-button"><Download className="h-4 w-4 mr-1" />Download PDF</Button>
          <Button size="sm" variant="ghost" onClick={downloadTxt} data-testid="index-download-txt-button"><Download className="h-4 w-4 mr-1" />Download TXT</Button>
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="index-dialog">
          <DialogHeader><DialogTitle className="font-heading">Folder Summary</DialogTitle><DialogDescription>{folder.name}</DialogDescription></DialogHeader>
          <p className="text-sm whitespace-pre-wrap leading-relaxed" data-testid="index-content">{md.split("\n").slice(1).join("\n")}</p>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function FolderView() {
  const { folders, notes, folderId, setNoteId, refresh, profile, assets } = useVault();
  const [busy, setBusy] = useState(false);
  const [autoScan, setAutoScan] = useAutoScan();
  const fileRef = useRef(null);
  const folder = folders.find((f) => f.id === folderId) || folders[0];
  const list = useMemo(() => notes.filter((n) => n.folderId === folder?.id), [notes, folder]);
  if (!folder) return null;
  const sorted = [...list].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const newNote = async () => { const n = await createNote(folder.id); await refresh(); setNoteId(n.id); };
  const importFiles = async (e) => {
    const picked = [...e.target.files]; e.target.value = "";
    if (!picked.length) return;
    const files = await Promise.all(picked.map(normalizeFile));
    setBusy(true);
    const texts = files.filter((f) => f.type === "text/plain" || /\.txt$/i.test(f.name));
    const media = files.filter((f) => !texts.includes(f));
    for (const f of texts) {
      const n = await createNote(folder.id, { title: f.name.replace(/\.txt$/i, ""), body: await f.text(), importedFrom: f.name });
      await stampVersion(n, profile, folder.name);
    }
    if (media.length) {
      const title = media.length === 1 ? media[0].name.replace(/\.[^.]+$/, "") : `Imported ${media.length} files`;
      let n = await createNote(folder.id, { title: autoScan ? "" : title, importedFrom: media.map((f) => f.name).join(", ") });
      for (const f of media) await addAttachment(n, f, { profile, assets });
      if (autoScan) {
        for (const f of media.filter((x) => ["photo", "pdf"].includes(kindOf(x.type, x.name)))) {
          const res = await scanWithToast(f, { name: f.name, mime: f.type });
          if (res) n = { ...n, ...scanPatch(n, res, f.name) };
        }
        if (!n.title) n.title = title;
        n = await saveDraft(n);
      }
      await stampVersion(n, profile, folder.name);
    }
    await refresh();
    setBusy(false);
    const count = texts.length + (media.length ? 1 : 0);
    toast.success(`Created ${count} note${count === 1 ? "" : "s"}`, { description: "Timestamped, hash-sealed & saved as Version 1" });
  };
  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-4xl" data-testid="folder-view">
      <p className="text-xs font-mono uppercase tracking-[0.2em] text-primary">Folder</p>
      <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1" data-testid="folder-title">{folder.name}</h1>
      <p className="text-sm text-muted-foreground mt-2">{list.length} entr{list.length === 1 ? "y" : "ies"} • stored only on this device</p>
      <div className="flex flex-wrap gap-2 mt-6">
        <Button onClick={newNote} data-testid="new-note-button"><Plus className="h-4 w-4 mr-1" />New Note</Button>
        <Button variant="outline" onClick={() => fileRef.current.click()} disabled={busy} data-testid="quick-import-button">{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}Quick Import (PDF, JPG, PNG, TXT)</Button>
        <span className="self-center ml-1"><AutoScanToggle on={autoScan} onChange={setAutoScan} testid="quick-import-autoscan-switch" /></span>
        <input ref={fileRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.txt,application/pdf,image/jpeg,image/png,text/plain" className="hidden" onChange={importFiles} data-testid="quick-import-input" />
      </div>
      <div className="mt-8 space-y-3">
        {folder.id === "proof-of-service" && list.length > 0 && <DeliveryBoard list={list} onOpen={setNoteId} />}
        <IndexCard folder={folder} list={list} profile={profile} />
        {sorted.map((n) => <NoteRow key={n.id} n={n} onOpen={() => setNoteId(n.id)} />)}
        {!sorted.length && <p className="text-sm text-muted-foreground py-10 text-center border border-dashed rounded-lg" data-testid="empty-folder">No notes yet. Create one, or quick-import PDFs, photos or text files.</p>}
      </div>
    </div>
  );
}
